import { useCallback, useEffect, useState } from "react";
import { ShieldCheck, ShieldAlert, Clock, RefreshCw, BadgeCheck, UserRound, Users } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const STATUS_STYLE = {
  approved: "bg-green-100 text-green-700",
  declined: "bg-red-100 text-red-700",
  rules_failed: "bg-red-100 text-red-700",
  expired: "bg-slate-100 text-slate-500",
  abandoned: "bg-slate-100 text-slate-500",
  review: "bg-amber-100 text-amber-700",
  submitted: "bg-blue-100 text-blue-700",
  started: "bg-blue-50 text-blue-600",
  created: "bg-slate-100 text-slate-500",
  reverification_requested: "bg-amber-100 text-amber-700",
};
const FAILED = ["declined", "expired", "abandoned", "rules_failed"];

export function IdentityPanel() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    api.get("/admin/identity/verifications").then((r) => setData(r.data)).catch(() => setData({ stats: {}, sessions: [] }));
  }, []);
  useEffect(() => { load(); }, [load]);

  const reverify = async (userId, email) => {
    setBusy(userId);
    try {
      await api.post(`/admin/identity/users/${userId}/request-reverification`);
      toast.success(`Re-verification requested — ${email} was notified by email.`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  if (!data) return <p className="text-slate-400 text-sm">Loading…</p>;
  const { stats, sessions } = data;
  const rows = sessions.filter((s) => {
    if (filter === "approved") return s.status === "approved";
    if (filter === "failed") return FAILED.includes(s.status);
    if (filter === "pending") return !FAILED.includes(s.status) && s.status !== "approved";
    return true;
  });

  const cards = [
    { icon: ShieldCheck, label: "Total checks", value: stats.total ?? 0, tint: "text-blue-600 bg-blue-50" },
    { icon: BadgeCheck, label: "Approved", value: stats.approved ?? 0, tint: "text-green-600 bg-green-50" },
    { icon: ShieldAlert, label: "Failed / declined", value: stats.failed ?? 0, tint: "text-red-500 bg-red-50" },
    { icon: Clock, label: "Pending", value: stats.pending ?? 0, tint: "text-amber-600 bg-amber-50" },
  ];

  return (
    <div data-testid="identity-panel" className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => (
          <div key={c.label} className="bg-white rounded-2xl shadow-card border border-slate-100 p-5 flex items-center gap-4">
            <span className={`grid place-items-center w-10 h-10 rounded-xl ${c.tint}`}><c.icon size={19} /></span>
            <div>
              <p className="font-heading text-2xl font-bold text-slate-900">{c.value}</p>
              <p className="text-slate-400 text-xs">{c.label}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex gap-2">
          {["all", "approved", "failed", "pending"].map((f) => (
            <button key={f} onClick={() => setFilter(f)} data-testid={`identity-filter-${f}`}
              className={`px-4 py-1.5 rounded-full text-sm font-semibold capitalize ${filter === f ? "bg-blue-600 text-white" : "bg-white text-slate-600 shadow-card"}`}>
              {f}
            </button>
          ))}
        </div>
        <button onClick={load} data-testid="identity-refresh" className="flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-blue-600"><RefreshCw size={15} /> Refresh</button>
      </div>

      <div className="bg-white rounded-2xl shadow-card border border-slate-100 overflow-x-auto">
        <table className="w-full text-sm min-w-[860px]">
          <thead>
            <tr className="text-left text-slate-400 text-xs uppercase tracking-wide border-b border-slate-100">
              <th className="px-5 py-3.5">Subject</th>
              <th className="px-5 py-3.5">Type</th>
              <th className="px-5 py-3.5">Status</th>
              <th className="px-5 py-3.5">License</th>
              <th className="px-5 py-3.5">Started / Decided</th>
              <th className="px-5 py-3.5">Session ID</th>
              <th className="px-5 py-3.5"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={7} className="px-5 py-10 text-center text-slate-400">No verification sessions {filter !== "all" ? `with status “${filter}”` : "yet"}.</td></tr>
            )}
            {rows.map((s) => {
              const lic = s.license_data || {};
              const failedRules = (s.rules || []).filter((r) => r.enabled && !r.passed);
              return (
                <tr key={s.id} data-testid={`identity-row-${s.id}`} className="border-b border-slate-50 hover:bg-slate-50/60">
                  <td className="px-5 py-3.5">
                    <p className="font-semibold text-slate-800 flex items-center gap-1.5">
                      {s.subject_name || "—"}
                      {s.user_verified && <BadgeCheck size={14} className="text-green-600" />}
                    </p>
                    <p className="text-slate-400 text-xs">{s.subject_email}</p>
                  </td>
                  <td className="px-5 py-3.5">
                    <span className="flex items-center gap-1.5 text-slate-600">
                      {s.subject_type === "driver" ? <Users size={14} className="text-slate-400" /> : <UserRound size={14} className="text-slate-400" />}
                      {s.subject_type === "driver" ? "Add'l driver" : "Renter"}
                    </span>
                  </td>
                  <td className="px-5 py-3.5">
                    <span className={`inline-block text-xs font-bold px-2.5 py-1 rounded-full ${STATUS_STYLE[s.status] || "bg-slate-100 text-slate-500"}`}>{s.status}</span>
                    {failedRules.length > 0 && <p className="text-red-500 text-[11px] mt-1">{failedRules[0].detail}</p>}
                    {(s.history || []).length > 1 && <p className="text-slate-400 text-[11px] mt-1">{s.history.length} events</p>}
                  </td>
                  <td className="px-5 py-3.5 text-slate-600 text-xs">
                    {lic.document_number ? (
                      <>
                        <p className="font-mono">{lic.document_number}</p>
                        <p className="text-slate-400">{[lic.issuing_state, lic.issuing_country].filter(Boolean).join(", ")}{lic.expiration_date ? ` · exp ${lic.expiration_date}` : ""}</p>
                      </>
                    ) : "—"}
                  </td>
                  <td className="px-5 py-3.5 text-slate-500 text-xs">
                    <p>{(s.created_at || "").slice(0, 10)}</p>
                    {s.decision_at && <p className="text-slate-400">→ {String(s.decision_at).slice(0, 10)}</p>}
                  </td>
                  <td className="px-5 py-3.5 font-mono text-[11px] text-slate-400" title={s.veriff_session_id}>{(s.veriff_session_id || "").slice(0, 8)}…</td>
                  <td className="px-5 py-3.5 text-right">
                    {s.user_id && (
                      <button onClick={() => reverify(s.user_id, s.subject_email)} disabled={busy === s.user_id}
                        data-testid={`identity-reverify-${s.user_id}`}
                        className="text-xs font-semibold text-amber-600 border border-amber-200 bg-amber-50 px-3 py-1.5 rounded-full hover:bg-amber-100 disabled:opacity-50 whitespace-nowrap">
                        {busy === s.user_id ? "Sending…" : "Re-verify"}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
